import { useState } from "react";
import { getAdminQueueItemApi, type AdminQueueItem, type AdminQueueItemDetail } from "@/portals/admin/api/adminReviewQueueApi";
import { adminQueryKeys } from "@/portals/admin/api/adminQueryKeys";
import { useAdminSocketEvent } from "@/lib/adminSocket";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { ReviewStatusBadge } from "@/features/clinician/review-queue/components/reviewBadges";
import {
  PatientIdentity,
  ReviewItemLayout,
  ReviewItemScreenMessage,
  ViewOnlyActionPanel,
} from "@/features/clinician/review-queue/components/ReviewItemLayout";
import { ASSIGN_MESSAGES, AssignClinicianModal } from "./AssignClinicianModal";

export const ADMIN_ITEM_MESSAGES = {
  note: "View only — assign this item to a clinician to have it reviewed.",
  notFound: ASSIGN_MESSAGES.itemGone,
  notAssignable: ASSIGN_MESSAGES.notAssignable,
  loadFailed: "Couldn't load this item. Please try again.",
};

interface AdminQueueItemViewProps {
  itemId: string;
  onBack: () => void;
  /** Assigned — the item has left the queue. */
  onAssigned: () => void;
}

/** An admin's read-only view of a shared-queue item, with Assign in place of the clinician's Claim. */
export function AdminQueueItemView({ itemId, onBack, onAssigned }: AdminQueueItemViewProps) {
  const [assigning, setAssigning] = useState(false);
  const [gone, setGone] = useState(false);

  // Cached per item: reopening it shows it at once, then refreshes it quietly.
  const query = useCachedQuery<AdminQueueItemDetail, { code?: string; message?: string } | null>(
    adminQueryKeys.reviewQueueItem(itemId),
    () => getAdminQueueItemApi(itemId),
  );
  const { cache } = query;
  // Live: a clinician claiming it (or anyone assigning it) changes the queue → refetch,
  // which turns into "already claimed or assigned" once it has left.
  useAdminSocketEvent("review_queue:changed", () => void query.refresh());

  // The item has left the queue: drop it from the list right away and forget this copy.
  const forgetItem = () => {
    const list = cache.get<AdminQueueItem[]>(adminQueryKeys.reviewQueue);
    if (list) cache.set(adminQueryKeys.reviewQueue, list.filter((i) => i.id !== itemId));
    cache.invalidate(adminQueryKeys.reviewQueueItem(itemId));
  };

  const back = { backLabel: "Review Queue", onBack };
  if (gone) {
    return <ReviewItemScreenMessage icon="lucide:user-check" message={ADMIN_ITEM_MESSAGES.notAssignable} {...back} />;
  }
  if (!query.data) {
    if (query.loading || !query.error) return <ReviewItemScreenMessage loading {...back} />;
    const code = query.error.code;
    return (
      <ReviewItemScreenMessage
        icon={code === "REVIEW_ITEM_NOT_FOUND" ? "lucide:file-x" : code === "ITEM_NOT_ASSIGNABLE" ? "lucide:user-check" : "lucide:wifi-off"}
        message={
          code === "REVIEW_ITEM_NOT_FOUND"
            ? ADMIN_ITEM_MESSAGES.notFound
            : code === "ITEM_NOT_ASSIGNABLE"
              ? ADMIN_ITEM_MESSAGES.notAssignable
              : ADMIN_ITEM_MESSAGES.loadFailed
        }
        {...back}
      />
    );
  }

  const item = query.data;
  return (
    <>
      <ReviewItemLayout
        item={item}
        {...back}
        identity={<PatientIdentity patient={item.patient} />}
        badges={<ReviewStatusBadge status={item.itemStatus} />}
        aside={
          <ViewOnlyActionPanel
            testId="admin-assign-panel"
            note={ADMIN_ITEM_MESSAGES.note}
            actionLabel="Assign to a clinician"
            onAction={() => setAssigning(true)}
          />
        }
      />
      <AssignClinicianModal
        itemId={assigning ? itemId : null}
        onClose={() => setAssigning(false)}
        onAssigned={() => {
          setAssigning(false);
          forgetItem();
          onAssigned();
        }}
        onItemGone={() => {
          setAssigning(false);
          forgetItem();
          setGone(true);
        }}
      />
    </>
  );
}
